import React, { Dispatch, SetStateAction, useCallback, useState } from 'react'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from '@/components/Filter/ClientFields/SelectClientFilter'
import { getDealsFields } from '@/components/Filter/getDealsFields'
import {
  SelectIntroducingBrokerModalOpenEvent,
  SelectIntroducingBrokersFilter,
} from '@/components/Filter/IntroducingBrokersFields/SelectIntroducingBrokersFilter'
import { SelectTradingAccountFilter } from '@/components/Filter/TradingAccountFields/SelectTradingAccountFilter'
import { SelectWalletModalOpenEvent } from '@/components/Filter/WalletFields/SelectWalletFilter'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import { DealDto, DealDtoFilterKey } from 'api/Clients/Types/DealDto'
import { Dialog } from '@/components/ui/dialog'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { useGridState } from 'features/DataGrid'
import { GridStateStatus } from 'features/DataGrid/hooks/useGridStatusState'
import { useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  selectGridFilterByFilterId,
  selectGridFilters,
} from 'store/grid'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContextData } from 'utils/TableStateContext'

import { SelectClientEvent, SelectClientTable } from '../../Records/Clients/SelectClientTable'
import { IntroducingBrokersModal } from '../../Records/IB/IntroducingBrokersModal'
import { SelectIntroducingBrokerEvent } from '../../Records/IB/IntroducingBrokersTable'
import SelectTradingAccountTable, {
  SelectTradingAccountEvent,
} from '../../Records/TradingAccounts/SelectTradingAccountTable'
import { normalizeDealsSearchQuery, normalizeDealsSortQuery } from './normalizer'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

interface DealsQuery {
  getQuery: (filter?: CompositeFilterDescriptor) => string | null
  getSort: (sortDescriptors?: SortDescriptor[]) => string | null
}

export const useDealsBuildQuery = (): DealsQuery => {
  const { gridState } = useGridState()

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      return buildQuery(normalizeDealsSearchQuery(gridFilter)) ?? null
    },
    [gridState.filter] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeDealsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  return { getQuery, getSort }
}

interface DealsPageProps {
  gridStatusState: GridStateStatus
  onChange(deals: DealDto[]): void
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
}

export const DealsTableFilter: React.FC<DealsPageProps> = (props) => {
  const { tableData, setTableData } = props
  const { gridStatusState, gridStatusActions } = props.gridStatusState
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [isIntroducingBrokersModalOpen, setIntroducingBrokersModalOpen] = useState<boolean>(false)
  const [introducingBrokerFilterId, setIntroducingBrokerFilterId] = useState<string>()

  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()

  const [isTradingAccountModalOpen, setTradingAccountModalOpen] = useState<boolean>(false)
  const [tradingAccountsModalFilterId, setTradingAccountsModalFilterId] = useState<string>()

  const { gridState, gridActions } = useGridState()
  const { gridFilterByFilterIdSelector } = useMapStateToProps()

  const { getSort, getQuery } = useDealsBuildQuery()

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await partnershipsApiClient.getDeals({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        props.onChange((response.items || []).map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [getQuery, getSort, gridActions, gridState.pageSize, gridState.sort, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleIntroducingBrokersModalOpen = (event: SelectIntroducingBrokerModalOpenEvent) => {
    setIntroducingBrokersModalOpen(true)
    setIntroducingBrokerFilterId(event.filterId)
  }

  const handleIntroducingBrokersModalClose = () => {
    setIntroducingBrokersModalOpen(false)
  }

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleTradingAccountModalOpen = (event: SelectWalletModalOpenEvent) => {
    setTradingAccountModalOpen(true)
    setTradingAccountsModalFilterId(event.filterId)
  }

  const handleTradingAccountModalClose = () => {
    setTradingAccountModalOpen(false)
    setTradingAccountsModalFilterId(undefined)
  }

  const handleSelectedTradingAccounts = (event: SelectTradingAccountEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleIntroducingBrokersSelect = (event: SelectIntroducingBrokerEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  return (
    <>
      {isIntroducingBrokersModalOpen && (
        <IntroducingBrokersModal
          filterId={introducingBrokerFilterId}
          onSelect={handleIntroducingBrokersSelect}
          onClose={handleIntroducingBrokersModalClose}
        />
      )}
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isTradingAccountModalOpen && (
        <Modal
          width={900}
          visible={isTradingAccountModalOpen}
          onClose={handleTradingAccountModalClose}
        >
          <SelectTradingAccountTable
            filterId={tradingAccountsModalFilterId}
            onSelect={handleSelectedTradingAccounts}
            onClose={handleTradingAccountModalClose}
          />
        </Modal>
      )}
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.DEALS}
        filterFields={[
          {
            name: DealDtoFilterKey.introducingBroker,
            label: 'Introducing Broker',
            filter: (ibFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectIntroducingBrokersFilter
                {...ibFilter}
                onIntroducingBrokerModalOpen={handleIntroducingBrokersModalOpen}
              >
                Choose IB
              </SelectIntroducingBrokersFilter>
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: DealDtoFilterKey.tradingAccount,
            label: 'Trading Account',
            filter: (taFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectTradingAccountFilter
                {...taFilter}
                onTaModalOpen={handleTradingAccountModalOpen}
              />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: DealDtoFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getDealsFields(),
        ]}
        containerClassName='records-clients__filter'
        getQuery={getQuery}
      />
    </>
  )
}
